import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Shield, Star } from "lucide-react";

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-pool-deep text-white">
      {/* Wave separator */}
      <div className="w-full overflow-hidden leading-none">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path
            d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z"
            fill="#0A2540"
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/gg35mjsd_Screenshot%202026-04-09%20at%208.48.30%E2%80%AFPM.png"
                alt="Pete's Pool Service & Repair"
                className="h-48 w-auto object-contain"
              />
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              Trusted pool maintenance and repair for Fresno & Clovis homeowners since 2019.
            </p>
            <div className="flex items-center gap-2 text-pool-aqua text-sm">
              <Shield size={14} />
              <span className="font-semibold">A+ BBB Rating</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-bold tracking-[0.15em] uppercase text-white/40 mb-6">
              Navigate
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Home", path: "/" },
                { label: "About Pete", path: "/about" },
                { label: "Our Services", path: "/services" },
                { label: "Contact Us", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                    className="text-white/60 hover:text-pool-aqua transition-colors duration-300 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-bold tracking-[0.15em] uppercase text-white/40 mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {[
                { name: "Tile Cleaning", hash: "tile" },
                { name: "Pool Maintenance", hash: "maintenance" },
                { name: "Equipment Repair", hash: "repair" },
                { name: "Filter Cleaning", hash: "filter" },
                { name: "Drain Clean-Ups", hash: "drain" },
                { name: "Green Pool Recovery", hash: "green-pool" },
              ].map((service) => (
                <li key={service.name}>
                  <Link
                    to={`/services#${service.hash}`}
                    className="text-white/60 hover:text-pool-aqua transition-colors duration-300 text-sm flex items-center gap-1.5"
                  >
                    {service.name}
                    {service.hash === "tile" && <Star size={12} className="text-gold fill-gold" />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-bold tracking-[0.15em] uppercase text-white/40 mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:5599996208"
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-white hover:text-pool-aqua transition-colors group"
                >
                  <div className="w-9 h-9 rounded-full bg-pool-aqua/10 flex items-center justify-center group-hover:bg-pool-aqua/20 transition-colors">
                    <Phone size={15} className="text-pool-aqua" />
                  </div>
                  <span className="text-sm font-semibold">(559) 999-6208</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                  <MapPin size={15} className="text-pool-aqua" />
                </div>
                <div className="text-sm text-white/60">
                  <p>Fresno, CA</p>
                  <p>Clovis, CA</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                  <Clock size={15} className="text-pool-aqua" />
                </div>
                <div className="text-sm text-white/60">
                  <p>Mon - Sat</p>
                  <p>7:00 AM - 6:00 PM</p>
                </div>
              </li>
            </ul>
            <p className="mt-4 text-sm font-accent italic text-pool-aqua/80">
              Se Habla Español
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs">
            &copy; {new Date().getFullYear()} Pete's Pool Service & Repair. All rights reserved.
          </p>
          <p className="text-white/30 text-xs">
            Proudly serving Fresno & Clovis, California
          </p>
        </div>
      </div>
    </footer>
  );
}
